<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useSalesStore } from 'dashboard/stores/sales';
import { useSaleTypesStore } from 'dashboard/stores/saleTypes';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['resolved-without-sale', 'sold', 'sale-created', 'sale-updated']);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const salesStore = useSalesStore();
const saleTypesStore = useSaleTypesStore();

onMounted(() => {
  if (!saleTypesStore.types.length) saleTypesStore.fetch();
});

const dialogRef = ref(null);
// mode: 'choice' | 'sale-form' | 'edit'
const mode = ref('choice');
const conversationDisplayId = ref(null);
const isSubmitting = ref(false);
const isEditMode = ref(false);
const resolveOnSave = ref(false);
const editingSaleId = ref(null);

const form = reactive({
  agentId: '',
  orderNumber: '',
  amount: '',
  saleType: '',
  returnInDays: '',
});

const pendingFollowUps = ref([]);
const markDoneFollowUps = ref(false);

const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  }))
);

const isFormValid = computed(
  () => form.agentId && form.orderNumber.trim() && Number(form.amount) > 0
);

function resetForm() {
  form.agentId = '';
  form.orderNumber = '';
  form.amount = '';
  form.saleType = '';
  form.returnInDays = '';
}

// Called from ResolveAction: shows choice screen first
const open = async displayId => {
  conversationDisplayId.value = displayId;
  isEditMode.value = false;
  resolveOnSave.value = true;

  // Check if sale already exists — if yes, skip modal and resolve directly
  try {
    await import('dashboard/api/sales').then(m =>
      m.default.getForConversation(displayId)
    );
    emit('resolved-without-sale');
    return;
  } catch {
    // 404 = no sale, open modal
  }

  mode.value = 'choice';
  resetForm();
  dialogRef.value?.open();
};

// Called from "Registrar venda" button or SaleInfoPanel: create without resolve
const openCreate = displayId => {
  conversationDisplayId.value = displayId;
  isEditMode.value = false;
  resolveOnSave.value = false;
  mode.value = 'sale-form';
  resetForm();
  dialogRef.value?.open();
};

// Called from SaleInfoPanel: edit existing sale
const openEdit = (displayId, existingSale) => {
  conversationDisplayId.value = displayId;
  isEditMode.value = true;
  resolveOnSave.value = false;
  editingSaleId.value = existingSale.id;
  mode.value = 'sale-form';
  form.agentId = existingSale.agent_id;
  form.orderNumber = existingSale.order_number;
  form.amount = String(existingSale.amount);
  form.saleType = existingSale.sale_type || '';
  form.returnInDays = '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const handleResolveWithoutSale = () => {
  close();
  emit('resolved-without-sale');
};

const handleShowSaleForm = () => {
  mode.value = 'sale-form';
};

const handleBack = () => {
  if (isEditMode.value) {
    close();
  } else {
    mode.value = 'choice';
  }
};

const handleConfirmSale = async () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;
  const payload = {
    agent_id: form.agentId,
    order_number: form.orderNumber.trim(),
    amount: Number(form.amount),
    sale_type: form.saleType || null,
  };

  try {
    if (isEditMode.value) {
      const updatedSale = await salesStore.updateSaleForConversation(
        conversationDisplayId.value,
        editingSaleId.value,
        payload
      );
      close();
      emit('sale-updated', updatedSale);
      useAlert(t('SALES.MODAL.UPDATE_SUCCESS'));
    } else {
      const newSale = await salesStore.createSaleForConversation(
        conversationDisplayId.value,
        payload
      );

      const { default: FollowUpsAPI } = await import('dashboard/api/followUps');

      if (form.returnInDays && Number(form.returnInDays) > 0) {
        const days = Number(form.returnInDays);
        const scheduled = new Date();
        scheduled.setDate(scheduled.getDate() + days);
        const scheduledFor = scheduled.toISOString().slice(0, 10);

        await FollowUpsAPI.create({
          conversation_id: newSale.conversation_id,
          scheduled_for: scheduledFor,
          source_type: 'Sale',
          source_id: newSale.id,
          agent_id: form.agentId,
        });
      } else {
        const { data: followUps } = await FollowUpsAPI.get({
          conversation_id: newSale.conversation_id,
        }).catch(() => ({ data: [] }));
        const pending = (Array.isArray(followUps) ? followUps : []).filter(
          f => f.status === 'pending'
        );
        if (pending.length > 0) {
          pendingFollowUps.value = pending;
          markDoneFollowUps.value = true;
          return;
        }
      }

      close();
      emit('sale-created', newSale);
      if (resolveOnSave.value) emit('sold');
      useAlert(t('SALES.MODAL.SUCCESS'));
    }
  } catch {
    useAlert(t('SALES.MODAL.ERROR'), { variant: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

const dialogTitle = computed(() => {
  if (isEditMode.value) return t('SALES.MODAL.EDIT_TITLE');
  if (mode.value === 'choice') return t('SALES.MODAL.TITLE');
  return t('SALES.MODAL.SALE_FORM_TITLE');
});

async function handleMarkDoneAndClose(shouldMark) {
  if (shouldMark && pendingFollowUps.value.length > 0) {
    const { default: FollowUpsAPI } = await import('dashboard/api/followUps');
    await Promise.allSettled(pendingFollowUps.value.map(f => FollowUpsAPI.markDone(f.id)));
  }
  markDoneFollowUps.value = false;
  pendingFollowUps.value = [];
  close();
  emit('sale-created');
  if (resolveOnSave.value) emit('sold');
  useAlert(t('SALES.MODAL.SUCCESS'));
}

defineExpose({ open, openCreate, openEdit, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <template #default>
      <!-- Tela de marcar retorno como feito -->
      <div v-if="markDoneFollowUps" class="flex flex-col gap-4 pt-2">
        <div class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3">
          <span class="i-lucide-calendar-check text-lg text-n-brand shrink-0 mt-0.5" />
          <div class="flex flex-col gap-0.5">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('SALES.FOLLOW_UP.MARK_DONE_PROMPT') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ pendingFollowUps.length === 1
                ? 'Há 1 retorno pendente nesta conversa.'
                : `Há ${pendingFollowUps.length} retornos pendentes nesta conversa.` }}
            </p>
          </div>
        </div>
        <div class="flex gap-2 justify-end mt-2">
          <Button
            :label="$t('SALES.MODAL.BACK')"
            ghost
            slate
            @click="handleMarkDoneAndClose(false)"
          />
          <Button
            :label="$t('SALES.FOLLOW_UP.DONE')"
            teal
            @click="handleMarkDoneAndClose(true)"
          />
        </div>
      </div>

      <!-- Tela de escolha (somente quando vem do ResolveAction) -->
      <div v-else-if="mode === 'choice'" class="flex flex-col gap-3 pt-2">
        <p class="text-sm text-n-slate-11">
          {{ t('SALES.MODAL.CHOICE_DESCRIPTION') }}
        </p>
        <div class="flex flex-col gap-2">
          <Button
            :label="t('SALES.MODAL.RESOLVE_WITHOUT_SALE')"
            ghost
            slate
            class="w-full justify-start"
            icon="i-lucide-check-circle"
            @click="handleResolveWithoutSale"
          />
          <Button
            :label="t('SALES.MODAL.SELL')"
            teal
            class="w-full justify-start"
            icon="i-lucide-badge-dollar-sign"
            @click="handleShowSaleForm"
          />
        </div>
      </div>

      <!-- Formulário de venda (create ou edit) -->
      <div v-else class="flex flex-col gap-4 pt-2">
        <ComboBox
          v-model="form.agentId"
          :label="t('SALES.MODAL.AGENT_LABEL')"
          :placeholder="t('SALES.MODAL.AGENT_PLACEHOLDER')"
          :options="agentOptions"
        />
        <Input
          v-model="form.orderNumber"
          :label="t('SALES.MODAL.ORDER_NUMBER_LABEL')"
          :placeholder="t('SALES.MODAL.ORDER_NUMBER_PLACEHOLDER')"
          type="text"
        />
        <Input
          v-model="form.amount"
          :label="t('SALES.MODAL.AMOUNT_LABEL')"
          :placeholder="t('SALES.MODAL.AMOUNT_PLACEHOLDER')"
          type="number"
          min="0"
          step="0.01"
        />
        <div v-if="saleTypesStore.types.length" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            {{ $t('SALES.SALE_TYPE.LABEL') }}
          </label>
          <select
            v-model="form.saleType"
            class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="">{{ $t('SALES.SALE_TYPE.PLACEHOLDER') }}</option>
            <option v-for="type in saleTypesStore.types" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </div>
        <div v-if="!isEditMode" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            {{ $t('SALES.FOLLOW_UP.SCHEDULE') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model="form.returnInDays"
              type="number"
              min="1"
              max="365"
              placeholder="—"
              class="w-24 rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
            <span class="text-sm text-n-slate-11">{{ $t('SALES.FOLLOW_UP.DAYS') }}</span>
          </div>
        </div>
        <div class="flex gap-2 justify-end mt-2">
          <Button
            :label="t('SALES.MODAL.BACK')"
            ghost
            slate
            @click="handleBack"
          />
          <Button
            :label="isEditMode ? t('SALES.MODAL.SAVE') : t('SALES.MODAL.CONFIRM_SALE')"
            teal
            :disabled="!isFormValid"
            :is-loading="isSubmitting"
            @click="handleConfirmSale"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
